import { Inject, Injectable, Injector } from '@angular/core';
import { assert } from '@sama/js-core';
import { map } from '@sama/micro-dash';
import { SubscriptionManager } from '@sama/rxjs-core';
import { BehaviorSubject } from 'rxjs';
import {
  ShapeLocationState,
  ShapeLocationState2D,
} from '../../../../cross-team-shared/annotation/shared/state/shape-location-state';
import { findKeyLocationIndex } from '../../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import { MessagingService } from '../../../../cross-team-shared/messaging/messaging.service';
import { isVideoOutput } from '../../../../cross-team-shared/misc-utils/output-utils';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../../team-apis/core/shell-connector';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { MLExtrapolationResponse } from '../../../../team-apis/raccoons/ml-extrapolation-response';
import { getShapeHelper } from '../../shape-helpers/get-shape-helper';
import { AbstractSensorContentStore2D } from '../../state/abstract-sensor-content-store-2d';
import { omitExtrapolation } from '../location-helper';
import { ExtrapolationRequest } from './extrapolation-request';

@Injectable()
export class ExtrapolationService extends SubscriptionManager {
  inFlightRequests$ = new BehaviorSubject<Set<ExtrapolationRequest>>(new Set());

  constructor(
    private store: AbstractSensorContentStore2D,
    private messagingService: MessagingService,
    @Inject(shellConnectorToken)
    private shellConnector: ShellConnector,
    private injector: Injector,
  ) {
    super();
    this.setUpInFlightRequestsSubscription();
  }

  /**
   * @returns the number of frames requested, to use for event tracking. Can be
   * 0, but only if the ml toggle button is "on"
   */
  updateExtrapolation(shapeIndex: number, startKeyFrame: number) {
    if (!isVideoOutput(this.store.state().output)) {
      return undefined; // core team only supports getMlAssistRange() in video
    }

    const assistRange = this.shellConnector.getMlAssistRange();

    // if ml toggle is off, should return undefined
    if (assistRange === 0) {
      return undefined;
    }

    const shape = this.store.state().shapes[shapeIndex];
    if (!getShapeHelper(shape.type).allowsMLExtrapolation) {
      return undefined;
    }

    this.removeExtrapolatedPoints(shapeIndex, startKeyFrame);

    const requestedExtrapolatedFrameCount = this.getNumberOfFramesWithinLimit(
      shape.key_locations,
      startKeyFrame,
      assistRange,
    );

    if (requestedExtrapolatedFrameCount) {
      void this.sendRequest(
        shapeIndex,
        startKeyFrame,
        requestedExtrapolatedFrameCount,
      );
    }
    return requestedExtrapolatedFrameCount;
  }

  addInFlightRequest(request: ExtrapolationRequest) {
    const inFlightRequests = this.inFlightRequests$.value;

    inFlightRequests.forEach((inFlightRequest) => {
      if (
        inFlightRequest.requestOptions.startKeyFrame ===
          request.requestOptions.startKeyFrame &&
        inFlightRequest.requestOptions.shapeIndex ===
          request.requestOptions.shapeIndex
      ) {
        this.deleteInFlightRequest(inFlightRequest);
      }
    });
    inFlightRequests.add(request);
    this.inFlightRequests$.next(inFlightRequests);
  }

  deleteInFlightRequest(request: ExtrapolationRequest) {
    const inFlightRequests = this.inFlightRequests$.getValue();
    inFlightRequests.delete(request);
    this.inFlightRequests$.next(inFlightRequests);
  }

  private async sendRequest(
    shapeIndex: number,
    startKeyFrame: number,
    requestedExtrapolatedFrameCount: number,
  ) {
    const requestOptions = {
      shapeIndex,
      startKeyFrame,
      requestedExtrapolatedFrameCount,
    };
    const mlRequest = new ExtrapolationRequest(requestOptions, this.injector);

    mlRequest
      .send()
      .then((response) => {
        if (response && this.inFlightRequests$.getValue().has(mlRequest)) {
          this.handleExtrapolationResponse(response, shapeIndex, startKeyFrame);
        }
      })
      .catch((error) => {
        console.error(error);
        this.messagingService.error(
          'ML Assist request failed. Check connection and retry or turn off ML Assist',
        );
      })
      .finally(() => {
        mlRequest.cleanUp();
      });
  }

  private removeExtrapolatedPoints(shapeIndex: number, frameNumber: number) {
    this.store('shapes')(shapeIndex)('key_locations').mutateUsing(
      (keyLocations) => {
        const index = findKeyLocationIndex(keyLocations, frameNumber);
        keyLocations[index] = omitExtrapolation(keyLocations[index]);
      },
    );
  }

  private handleExtrapolationResponse(
    body: MLExtrapolationResponse,
    shapeIndex: number,
    frameNumber: number,
  ) {
    const keyLocationStore = this.store('shapes')(shapeIndex)('key_locations');

    const extrapolation = this.getExtrapolationFromResponse(
      body,
      shapeIndex,
      frameNumber,
      keyLocationStore.state(),
    );

    if (!extrapolation.points.length) {
      return;
    }
    const keyIndex = findKeyLocationIndex(
      keyLocationStore.state(),
      frameNumber,
    );
    keyLocationStore(keyIndex)('extrapolation').set(extrapolation);
    this.shellConnector.setShapes(this.store.state());
    this.shellConnector.pushAndTrack(
      { id: 'load_ml_extrapolation_response', bucket: 'annotation' },
      { isInteractive: false },
    );
  }

  private getExtrapolationFromResponse(
    body: MLExtrapolationResponse,
    shapeIndex: number,
    frameNumber: number,
    keyLocations: Array<ShapeLocationState<Point2D>>,
  ) {
    const prunedResponseLocations = body.key_locations.slice(
      0,
      this.getNumberOfFramesWithinLimit(
        keyLocations,
        frameNumber,
        body.key_locations.length,
      ),
    );
    const extrapolatedPoints = map(prunedResponseLocations, (location) =>
      getShapeHelper(
        this.store.state().shapes[shapeIndex].type,
      ).pointsStateToAnswer(location.points),
    );

    const { output } = this.store.state();
    assert(isVideoOutput(output), 'Requests will only be sent in VideoOutputs');

    return {
      points: extrapolatedPoints,
      source: {
        name: 'ml-extrapolation',
        model_id: output.meta.ml_extrapolation_options.model_id,
      },
    };
  }

  private getNumberOfFramesWithinLimit(
    locations: ShapeLocationState2D[],
    startKeyframe: number,
    requestedFramesCount: number,
  ) {
    const keyIndex = findKeyLocationIndex(locations, startKeyframe);
    if (keyIndex === -1) {
      const message = `Missing key location for frame ${startKeyframe} in getNumberOfFramesWithinLimit()`;
      throw new Error(message);
    }

    // The limit is either the next keyframe or the last frame.
    const limit =
      locations[keyIndex + 1]?.frame_number ||
      this.store.state().sensorRendererState.sensorFrames.length;

    return Math.min(requestedFramesCount, limit - startKeyframe - 1);
  }

  private setUpInFlightRequestsSubscription() {
    this.subscribeTo(this.inFlightRequests$, (inFlightRequests) => {
      this.shellConnector.updateInFlightMlExtrapolationRequests(
        map(Array.from(inFlightRequests), (request) => {
          const options = request.requestOptions;
          return {
            shapeId: options.shapeIndex,
            keyFrameNumber: options.startKeyFrame,
            requestedExtrapolatedFramesCount:
              options.requestedExtrapolatedFrameCount,
          };
        }),
      );
    });
  }
}
